import React, { useState } from 'react';
import { FaLock, FaKey, FaEye, FaEyeSlash, FaShieldAlt, FaCheckCircle, FaTimesCircle, FaUserCircle } from 'react-icons/fa';
import Swal from 'sweetalert2';
import api from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';
import './ChangePassword.css';

const STRENGTH_LEVELS = [
  { label: 'Very Weak',   color: '#ef4444', width: '20%'  },
  { label: 'Weak',        color: '#f97316', width: '40%'  },
  { label: 'Fair',        color: '#eab308', width: '60%'  },
  { label: 'Strong',      color: '#22c55e', width: '80%'  },
  { label: 'Very Strong', color: '#16a34a', width: '100%' },
];

const getStrength = (pwd) => {
  if (!pwd) return -1;
  let score = 0;
  if (pwd.length >= 8)          score++;
  if (pwd.length >= 12)         score++;
  if (/[A-Z]/.test(pwd))        score++;
  if (/[0-9]/.test(pwd))        score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  return Math.min(score - 1, 4);
};

const RULES = [
  { label: 'At least 6 characters',  test: p => p.length >= 6 },
  { label: 'One uppercase letter',    test: p => /[A-Z]/.test(p) },
  { label: 'One number',              test: p => /[0-9]/.test(p) },
  { label: 'One special character',   test: p => /[^A-Za-z0-9]/.test(p) },
];

const ChangePassword = () => {
  const { user } = useAuth();

  const [form, setForm]       = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [show, setShow]       = useState({ current: false, new: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const strengthIdx = getStrength(form.newPassword);
  const strength    = strengthIdx >= 0 ? STRENGTH_LEVELS[strengthIdx] : null;
  const mismatch    = form.confirmPassword && form.newPassword !== form.confirmPassword;
  const match       = form.confirmPassword && form.newPassword === form.confirmPassword;

  const set = (field, val) => {
    setSuccess(false);
    setForm(p => ({ ...p, [field]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { currentPassword, newPassword, confirmPassword } = form;

    if (!currentPassword || !newPassword || !confirmPassword)
      return Swal.fire('Required', 'Please fill in all fields.', 'warning');
    if (newPassword.length < 6)
      return Swal.fire('Too Short', 'New password must be at least 6 characters.', 'warning');
    if (newPassword !== confirmPassword)
      return Swal.fire('Mismatch', 'New password and confirm password do not match.', 'error');
    if (currentPassword === newPassword)
      return Swal.fire('Invalid', 'New password cannot be the same as your current password.', 'warning');

    setLoading(true);
    try {
      const { data } = await api.put('/auth/password', { currentPassword, newPassword });

      // Backend returns { success, token, refreshToken, user } via sendToken()
      if (data.token) {
        localStorage.setItem('token', data.token);
        if (user?.role) localStorage.setItem(`token_${user.role}`, data.token);
      }
      if (data.refreshToken) {
        localStorage.setItem('refreshToken', data.refreshToken);
      }
      if (data.user) {
        const updatedUser = { ...user, ...data.user };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        if (user?.role) localStorage.setItem(`user_${user.role}`, JSON.stringify(updatedUser));
      }

      setSuccess(true);
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShow({ current: false, new: false, confirm: false });

      Swal.fire({
        title: 'Password Updated!',
        text: 'Your password has been changed successfully.',
        icon: 'success',
        confirmButtonColor: '#3b82f6',
        timer: 3000,
        timerProgressBar: true,
      });
    } catch (err) {
      Swal.fire(
        'Error',
        err.response?.data?.message || 'Failed to change password. Please verify your current password.',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="change-password-page">
      <div className="change-password-card">

        {/* Header */}
        <div className="change-password-header">
          <div className="change-password-icon-ring">
            <FaShieldAlt />
          </div>
          <h2 className="change-password-title">Change Password</h2>
          <p className="change-password-subtitle">Keep your account secure with a strong, unique password.</p>
        </div>

        {/* User info card */}
        {user && (
          <div className="change-password-user-card">
            <div className="change-password-user-avatar">
              {user.name ? user.name[0].toUpperCase() : <FaUserCircle />}
            </div>
            <div className="change-password-user-info">
              <div className="change-password-user-name">{user.name || '—'}</div>
              <div className="change-password-user-email">{user.email}</div>
            </div>
            <span className="change-password-user-role">{user.role}</span>
          </div>
        )}

        {/* Success banner */}
        {success && (
          <div className="change-password-success-banner">
            <FaCheckCircle className="success-icon" />
            <span>Password updated successfully!</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="change-password-form" autoComplete="off">

          {/* Current Password */}
          <div className="change-password-group">
            <label className="change-password-label">Current Password</label>
            <div className="change-password-input-wrap">
              <FaLock className="change-password-input-icon" />
              <input
                type={show.current ? 'text' : 'password'}
                placeholder="Enter current password"
                value={form.currentPassword}
                onChange={e => set('currentPassword', e.target.value)}
                className="change-password-input"
                autoComplete="current-password"
              />
              <button type="button" className="change-password-eye-btn"
                onClick={() => setShow(p => ({ ...p, current: !p.current }))} tabIndex={-1}>
                {show.current ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <div className="change-password-divider" />

          {/* New Password */}
          <div className="change-password-group">
            <label className="change-password-label">New Password</label>
            <div className="change-password-input-wrap">
              <FaKey className="change-password-input-icon" />
              <input
                type={show.new ? 'text' : 'password'}
                placeholder="Create a strong password"
                value={form.newPassword}
                onChange={e => set('newPassword', e.target.value)}
                className="change-password-input"
                autoComplete="new-password"
              />
              <button type="button" className="change-password-eye-btn"
                onClick={() => setShow(p => ({ ...p, new: !p.new }))} tabIndex={-1}>
                {show.new ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            {/* Strength bar */}
            {form.newPassword && strength && (
              <div className="change-password-strength-container">
                <div className="change-password-strength-bg">
                  <div className="change-password-strength-fill"
                    style={{ width: strength.width, backgroundColor: strength.color }} />
                </div>
                <span className="change-password-strength-text" style={{ color: strength.color }}>
                  {strength.label}
                </span>
              </div>
            )}

            {/* Rules box */}
            <div className="change-password-rules-box">
              <p className="rules-title">Password requirements:</p>
              <ul className="change-password-rules-list">
                {RULES.map(r => (
                  <li key={r.label} className={r.test(form.newPassword) ? 'pass' : 'fail'}>
                    {r.test(form.newPassword) ? <FaCheckCircle /> : <FaTimesCircle />}
                    {r.label}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="change-password-group">
            <label className="change-password-label">Confirm New Password</label>
            <div className={`change-password-input-wrap ${mismatch ? 'error' : match ? 'success' : ''}`}>
              <FaKey className="change-password-input-icon" />
              <input
                type={show.confirm ? 'text' : 'password'}
                placeholder="Re-enter new password"
                value={form.confirmPassword}
                onChange={e => set('confirmPassword', e.target.value)}
                className="change-password-input"
                autoComplete="new-password"
              />
              <button type="button" className="change-password-eye-btn"
                onClick={() => setShow(p => ({ ...p, confirm: !p.confirm }))} tabIndex={-1}>
                {show.confirm ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {mismatch && <span className="change-password-feedback error">⚠ Passwords do not match</span>}
            {match    && <span className="change-password-feedback success">✓ Passwords match</span>}
          </div>

          <button type="submit" className="change-password-submit-btn" disabled={loading || !!mismatch}>
            {loading
              ? <><span className="change-password-spinner" /> Updating Securely...</>
              : 'Update Password'}
          </button>

        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
